import type {
  LaunchReadiness,
  PacketCompleteness,
  SessionUser,
  SubmissionQualification,
  SubmissionStatus,
  UpsertQualificationInput,
} from '@motoveo/shared';
import {
  LIEN_STATUSES,
  PRICE_PRIORITIES,
  RETAIL_EXPECTATION_RISKS,
  SUBMISSION_STATUSES,
} from '@motoveo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { dollarsToCents, formatCents, formatDate, formatMiles } from '../lib/format';

/** '' | 'yes' | 'no' → null | true | false (a nullable-boolean select). */
const triBool = (v: string): boolean | null => (v === 'yes' ? true : v === 'no' ? false : null);
const fromBool = (b: boolean | null | undefined): string => (b == null ? '' : b ? 'yes' : 'no');

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <span className="field-value">{value ?? '—'}</span>
    </div>
  );
}

/** Ranked dealer matches for the submission's resolved spec (READ-ONLY preview; sends nothing). */
function MatchesPanel({ submissionId }: { submissionId: string }) {
  const matches = useQuery({
    queryKey: ['matches', submissionId],
    queryFn: () => api.matches.forSubmission(submissionId),
  });

  return (
    <div className="card">
      <div className="row-between">
        <h3>Dealer matches (preview)</h3>
        <span className="inline">
          {matches.data ? <span className="muted">{matches.data.match_count} matched</span> : null}
          <Link to={`/submissions/${submissionId}/blast`}>Compose blast →</Link>
        </span>
      </div>
      {matches.isLoading ? (
        <p className="muted">Matching…</p>
      ) : matches.isError ? (
        <p className="error">{(matches.error as Error).message}</p>
      ) : !matches.data?.spec_ready ? (
        <p className="muted">Decode the vehicle first — matching needs a resolved spec.</p>
      ) : matches.data.matches.length === 0 ? (
        <p className="muted">No active dealer ISO prefs match this spec yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Score</th>
                <th>Dealer</th>
                <th>State</th>
                <th>ISO pref</th>
                <th>Buyer-in-hand</th>
                <th>Contact</th>
                <th>Reachable</th>
                <th>Why matched</th>
              </tr>
            </thead>
            <tbody>
              {matches.data.matches.map((m) => {
                const reasons = m.reasons as {
                  hard?: Record<string, boolean>;
                  soft?: { preferred_option_codes_matched?: string[]; exterior_color_matched?: boolean; budget_within?: boolean | null };
                };
                const hardFacets = Object.keys(reasons.hard ?? {});
                const soft = reasons.soft ?? {};
                return (
                  <tr key={`${m.dealer_id}-${m.iso_pref_id}`} className={m.has_named_buyer ? 'wedge-row' : ''}>
                    <td>
                      <strong>{m.score.toFixed(1)}</strong>
                    </td>
                    <td>
                      <Link to={`/dealers/${m.dealer_id}`}>{m.dealer_business_name}</Link>
                    </td>
                    <td className="muted">{m.dealer_state}</td>
                    <td>{m.iso_pref_label}</td>
                    <td>{m.has_named_buyer ? <span className="pill conf-high">buyer waiting</span> : <span className="muted">—</span>}</td>
                    <td className="muted">{m.contact_name ?? m.contact_phone_e164 ?? m.contact_email ?? '—'}</td>
                    <td>{m.contact_reachable ? <span className="ok-dot">●</span> : <span className="warn" title="capture consent on the dealer">—</span>}</td>
                    <td className="muted">
                      {hardFacets.length ? hardFacets.join(', ') : 'any'}
                      {soft.preferred_option_codes_matched?.length ? ` · +opts ${soft.preferred_option_codes_matched.join('/')}` : ''}
                      {soft.exterior_color_matched ? ' · +color' : ''}
                      {soft.budget_within === false ? ' · over budget' : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * M10 — the LAUNCH READINESS card: the gate verdict (ready/blocked), the packet checklist, franchise-
 * network coverage (refined model: reachable active franchise dealers gate the launch; ISO matches are
 * an optional priority signal), price realism, and — surfaced PROMINENTLY — the seller's retail-anchor
 * risk + whether they accept a dealer-direct offer (so a founder never launches a mis-anchored seller).
 */
function LaunchReadinessCard({ readiness, packet }: { readiness: LaunchReadiness; packet: PacketCompleteness }) {
  const risk = readiness.retail_expectation_risk;
  return (
    <div className={`card ${readiness.ready ? '' : 'warn-card'}`}>
      <div className="row-between">
        <h3>
          Launch readiness{' '}
          {readiness.ready ? <span className="pill conf-high">READY</span> : <span className="pill conf-low">BLOCKED</span>}
        </h3>
        <span className="muted">
          packet {packet.score}/{packet.max} · network {readiness.reachable_dealer_count}/{readiness.active_franchise_dealer_count} reachable
          · {readiness.matched_dealer_count} ISO match{readiness.matched_dealer_count === 1 ? '' : 'es'}
          {readiness.named_buyer_count > 0 ? ` · ${readiness.named_buyer_count} buyer-in-hand` : ''}
        </span>
      </div>

      {/* prominent seller-anchor surfacing (§4B — the biggest close risk) */}
      {risk === 'high' ? (
        <p className="disclosure" style={{ color: '#f4a0a0', borderLeft: '2px solid var(--down)' }}>
          ⚠ Seller is anchored to RETAIL comps. Accepts a dealer-direct offer:{' '}
          <strong>{readiness.accepts_dealer_direct == null ? 'unknown' : readiness.accepts_dealer_direct ? 'yes' : 'NO'}</strong>. Confirm
          before launching — do not send a seller who only wants record retail pricing.
        </p>
      ) : null}

      {readiness.blockers.length > 0 ? (
        <p>
          {readiness.blockers.map((b) => (
            <span key={b} className="pill conf-low">
              {b}
            </span>
          ))}
        </p>
      ) : null}
      {readiness.warnings.length > 0 ? (
        <p>
          {readiness.warnings.map((w) => (
            <span key={w} className="pill conf-medium">
              {w}
            </span>
          ))}
        </p>
      ) : null}

      {readiness.reasons.length > 0 ? (
        <ul className="tight">
          {readiness.reasons.map((r, i) => (
            <li key={i} className="muted">
              {r}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">All four launch gates pass — this car is ready to compose a blast.</p>
      )}

      <h4>Packet checklist</h4>
      <ul className="tight">
        {packet.checklist.map((c) => (
          <li key={c.key}>
            {c.ok ? <span className="ok-dot">●</span> : <span className="warn">—</span>} {c.label}{' '}
            <span className="muted">({c.detail})</span>
          </li>
        ))}
      </ul>

      <div className="field">
        <span className="field-label">Price realism</span>
        <span className="field-value">
          <span className={`pill ${readiness.price_realism.status === 'ok' ? 'conf-high' : readiness.price_realism.status === 'above_band' ? 'conf-low' : 'conf-medium'}`}>
            {readiness.price_realism.status.replace('_', ' ')}
          </span>{' '}
          <span className="muted">{readiness.price_realism.detail}</span>
        </span>
      </div>
    </div>
  );
}

/** M10 — capture/edit the seller-expectation qualification (the launch-gate + control-tower input). */
function QualificationEditor({ submissionId, qualification }: { submissionId: string; qualification: SubmissionQualification | null }) {
  const queryClient = useQueryClient();
  const q = qualification;
  const [retailRisk, setRetailRisk] = useState(q?.retail_expectation_risk ?? '');
  const [acceptsDirect, setAcceptsDirect] = useState(fromBool(q?.accepts_dealer_direct));
  const [pricePriority, setPricePriority] = useState(q?.price_priority ?? '');
  const [floor, setFloor] = useState(q?.seller_floor_cents != null ? String(q.seller_floor_cents / 100) : '');
  const [lien, setLien] = useState(q?.lien_status ?? '');
  const [ownership, setOwnership] = useState(fromBool(q?.ownership_confirmed));
  const [ready, setReady] = useState(fromBool(q?.ready_to_transact));
  const [motivation, setMotivation] = useState(q?.sell_motivation ?? '');
  const [notes, setNotes] = useState(q?.qualification_notes ?? '');

  const save = useMutation({
    mutationFn: (body: UpsertQualificationInput) => api.submissions.setQualification(submissionId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['submission', submissionId] });
    },
  });

  const onSave = () =>
    save.mutate({
      retail_expectation_risk: (retailRisk || null) as UpsertQualificationInput['retail_expectation_risk'],
      accepts_dealer_direct: triBool(acceptsDirect),
      price_priority: (pricePriority || null) as UpsertQualificationInput['price_priority'],
      seller_floor_cents: dollarsToCents(floor),
      lien_status: (lien || null) as UpsertQualificationInput['lien_status'],
      ownership_confirmed: triBool(ownership),
      ready_to_transact: triBool(ready),
      sell_motivation: motivation.trim() || null,
      qualification_notes: notes.trim() || null,
    });

  return (
    <div className="card">
      <div className="row-between">
        <h3>Seller qualification</h3>
        {q ? <span className="muted">updated {formatDate(q.updated_at)}</span> : <span className="warn">not yet qualified</span>}
      </div>
      <div className="form-grid">
        <label>
          Retail-expectation risk
          <select value={retailRisk} onChange={(e) => setRetailRisk(e.target.value)}>
            <option value="">—</option>
            {RETAIL_EXPECTATION_RISKS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label>
          Accepts dealer-direct?
          <select value={acceptsDirect} onChange={(e) => setAcceptsDirect(e.target.value)}>
            <option value="">—</option>
            <option value="yes">yes</option>
            <option value="no">no</option>
          </select>
        </label>
        <label>
          Price priority
          <select value={pricePriority} onChange={(e) => setPricePriority(e.target.value)}>
            <option value="">—</option>
            {PRICE_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label>
          Seller floor ($ — moves it now)
          <input value={floor} onChange={(e) => setFloor(e.target.value)} placeholder="e.g. 185000" inputMode="decimal" />
        </label>
        <label>
          Lien / payoff status
          <select value={lien} onChange={(e) => setLien(e.target.value)}>
            <option value="">—</option>
            {LIEN_STATUSES.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label>
          Ownership confirmed?
          <select value={ownership} onChange={(e) => setOwnership(e.target.value)}>
            <option value="">—</option>
            <option value="yes">yes</option>
            <option value="no">no</option>
          </select>
        </label>
        <label>
          Ready to transact now?
          <select value={ready} onChange={(e) => setReady(e.target.value)}>
            <option value="">—</option>
            <option value="yes">yes</option>
            <option value="no">no</option>
          </select>
        </label>
      </div>
      <label className="form" style={{ marginTop: 12 }}>
        <span className="hint">Sell motivation / timeline</span>
        <textarea value={motivation} onChange={(e) => setMotivation(e.target.value)} rows={2} />
      </label>
      <label className="form">
        <span className="hint">Qualification notes</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </label>
      <div className="inline" style={{ marginTop: 12 }}>
        <button onClick={onSave} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save qualification'}
        </button>
        {save.isError ? <span className="error">{(save.error as Error).message}</span> : null}
        {save.isSuccess ? <span className="ok-dot">● saved</span> : null}
      </div>
    </div>
  );
}

export function SubmissionDetailPage({ user }: { user: SessionUser }) {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['submission', id],
    queryFn: () => api.submissions.get(id),
    enabled: Boolean(id),
  });

  const update = useMutation({
    mutationFn: (patch: { status?: SubmissionStatus }) => api.submissions.update(id, patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['submission', id] });
      await queryClient.invalidateQueries({ queryKey: ['submissions'] });
    },
  });

  const decode = useMutation({
    mutationFn: () => api.submissions.decode(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['submission', id] });
    },
  });

  return (
    <Layout user={user}>
      <p>
        <Link to="/submissions" className="muted">
          ← Submissions
        </Link>
      </p>

      {detail.isLoading ? (
        <p className="muted">Loading…</p>
      ) : detail.isError ? (
        <p className="error">{(detail.error as Error).message}</p>
      ) : detail.data ? (
        (() => {
          const { submission: s, vehicle: v, media, consent, spec, options, qualification, packet, launch_readiness, valuation } = detail.data;
          const highValue = options.filter((o) => o.is_high_value);
          return (
            <>
              <div className="row-between">
                <h2>
                  {s.public_ref} <span className="pill">{s.status}</span>
                </h2>
                <div className="inline">
                  {v ? (
                    <button onClick={() => decode.mutate()} disabled={decode.isPending}>
                      {decode.isPending ? 'Decoding…' : 'Decode now'}
                    </button>
                  ) : null}
                  <Link to={`/submissions/${id}/valuation`}>
                    Appraisal worksheet
                    {valuation ? <span className={`pill ${valuation.verdict === 'closable' ? 'conf-high' : valuation.verdict === 'unrealistic' ? 'conf-low' : ''}`}> {valuation.verdict}</span> : null} →
                  </Link>
                  <Link to={`/submissions/${id}/offers`}>Build offer menu →</Link>
                  <label className="inline">
                    Set status
                    <select
                      value={s.status}
                      disabled={update.isPending}
                      onChange={(e) => update.mutate({ status: e.target.value as SubmissionStatus })}
                    >
                      {SUBMISSION_STATUSES.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
              {update.isError ? <p className="error">{(update.error as Error).message}</p> : null}
              {decode.isError ? <p className="error">{(decode.error as Error).message}</p> : null}

              {/* M10 — launch readiness (prominent) + the seller-expectation qualification editor. */}
              <LaunchReadinessCard readiness={launch_readiness} packet={packet} />
              <QualificationEditor key={qualification?.updated_at ?? 'none'} submissionId={s.id} qualification={qualification} />

              {v ? (
                <div className="card">
                  <div className="row-between">
                    <h3>
                      Resolved spec{' '}
                      {spec ? <span className={`pill conf-${spec.confidence}`}>{spec.confidence}</span> : null}
                    </h3>
                    <Link to={`/vehicles/${v.id}/spec`}>Open spec review →</Link>
                  </div>
                  {spec ? (
                    <section className="grid-2">
                      <Field label="Designation" value={<strong>{spec.model_designation ?? '—'}</strong>} />
                      <Field
                        label="Transmission"
                        value={`${spec.transmission} (${spec.transmission_confidence})`}
                      />
                      <Field label="Exterior" value={spec.paint_to_sample ? `PTS ${spec.pts_color_name ?? ''}`.trim() : spec.exterior_color} />
                      <Field label="Interior" value={[spec.interior_color, spec.interior_material].filter(Boolean).join(' · ') || null} />
                      <Field label="Total MSRP" value={formatCents(spec.total_msrp_cents)} />
                      <Field label="Options" value={`${options.length} (${highValue.length} high-value)`} />
                      <Field label="Primary source" value={v.primary_spec_source} />
                      <Field label="Fingerprint" value={spec.spec_fingerprint ? <code>{spec.spec_fingerprint}</code> : '—'} />
                    </section>
                  ) : (
                    <p className="muted">Not decoded yet — press “Decode now” to run vPIC + resolve the spec.</p>
                  )}
                  {highValue.length > 0 ? (
                    <p>
                      {highValue.map((o) => (
                        <span key={o.id} className="pill conf-high" title={o.description_snapshot ?? ''}>
                          {o.normalized_code}
                        </span>
                      ))}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {v && spec ? <MatchesPanel submissionId={s.id} /> : null}

              <section className="grid-2">
                <div className="card">
                  <h3>Vehicle</h3>
                  <Field label="VIN (raw)" value={<code>{s.raw_vin}</code>} />
                  <Field
                    label="VIN valid (check digit)"
                    value={v ? (v.vin_valid ? 'yes' : <span className="warn">no</span>) : '—'}
                  />
                  <Field
                    label="Decoded"
                    value={v ? `${v.model_year ?? ''} ${v.make ?? ''} ${v.model ?? ''}`.trim() || '—' : '—'}
                  />
                  <Field label="Generation" value={v?.series_generation} />
                  <Field label="Decode status" value={v?.decode_status} />
                </div>

                <div className="card">
                  <h3>Condition &amp; price</h3>
                  <Field label="Grade" value={s.condition_grade} />
                  <Field label="Title" value={s.title_status} />
                  <Field label="Mileage" value={formatMiles(s.stated_mileage)} />
                  <Field label="Asking" value={formatCents(s.asking_price_cents)} />
                  <Field label="Trade target" value={formatCents(s.trade_target_cents)} />
                  <Field label="Accident disclosed" value={s.accident_disclosed == null ? '—' : String(s.accident_disclosed)} />
                  <Field label="Modifications" value={s.has_modifications == null ? '—' : String(s.has_modifications)} />
                </div>

                <div className="card">
                  <h3>Seller</h3>
                  <Field label="Name" value={s.seller_name} />
                  <Field label="Phone" value={s.seller_phone_e164} />
                  <Field label="Email" value={s.seller_email} />
                  <Field label="Region" value={s.seller_region_label ?? s.seller_state} />
                  <Field label="Source" value={s.source} />
                  <Field label="Created" value={formatDate(s.created_at)} />
                </div>

                <div className="card">
                  <h3>PEWC consent proof</h3>
                  {consent ? (
                    <>
                      <Field label="Action" value={<span className="ok-dot">●</span>} />
                      <Field label="Channel" value={consent.channel} />
                      <Field label="Type" value={consent.consent_type} />
                      <Field label="Captured" value={formatDate(consent.captured_at)} />
                      <Field label="Source" value={consent.capture_source} />
                      <Field label="Version" value={consent.consent_text_version} />
                      <Field label="IP" value={consent.ip_address} />
                      <p className="disclosure">{consent.consent_text_snapshot}</p>
                    </>
                  ) : (
                    <p className="warn">No PEWC consent on record — do not send marketing SMS.</p>
                  )}
                </div>
              </section>

              {s.condition_notes ? (
                <div className="card">
                  <h3>Condition notes</h3>
                  <p>{s.condition_notes}</p>
                </div>
              ) : null}

              <div className="card">
                <h3>Media ({media.length})</h3>
                {media.length > 0 ? (
                  <div className="thumbs">
                    {media.map((m) => (
                      <figure key={m.id} className="thumb">
                        <img src={api.submissions.mediaRawUrl(s.id, m.id)} alt={m.kind} loading="lazy" />
                        <figcaption className="muted">
                          {m.kind}
                          {m.exif_stripped ? ' · EXIF stripped' : ''}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                ) : (
                  <p className="muted">No photos or window sticker uploaded yet.</p>
                )}
              </div>
            </>
          );
        })()
      ) : null}
    </Layout>
  );
}
